import Icon from "@/v2-ui/icon"
import { DiffLine, diffWords } from "@/v2-backoffice-client/components/diff"
import LayerDriftResolveActions from "@/v2-backoffice-client/layer/drift/resolve/LayerDriftResolveActions"
import type { LayerDriftEntry } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"

export type LayerDriftEntryRowProps = {
  entry: LayerDriftEntry
}

const WAS_TITLE =
  "The value recorded in the accepted baseline — what this layer was built against."

const NOW_TITLE = "The value the service returns today."

/**
 * One drifted config key, rendered as the same git-style diff the text views
 * use: the path it lives at, the baseline value in red, today's value in
 * green, then one line of prose about it.
 *
 * Every entry is exactly two diff lines whether the key was added, removed or
 * changed — the absent side shows a placeholder instead of switching to a
 * different layout. Twenty entries then have one rhythm to scan (red above
 * green, values always in the same column) rather than three, which is what
 * the earlier card-per-entry version got wrong.
 */
function LayerDriftEntryRow(props: LayerDriftEntryRowProps) {
  const { entry } = props

  // Empty string for an absent side, so diffWords marks a one-sided change as
  // wholly added/removed and the opposite line falls through to its
  // placeholder.
  const segments = diffWords(entry.Before ?? "", entry.After ?? "")

  return (
    <article className="diff-field">
      <code className="layer-drift-entry-path">{entry.Path}</code>

      <DiffLine
        side="old"
        origin="was"
        originTitle={WAS_TITLE}
        segments={segments}
        empty="(none)"
      />
      <DiffLine
        side="new"
        origin="now"
        originTitle={NOW_TITLE}
        segments={segments}
        empty="(none)"
      />

      {/* The classifier's reason ("geometryType changed") is a shorter
          restatement of the suggestion, which spells out both what moved and
          what to do — so only the better sentence is shown. Amber with a bulb
          when it is advice, plain when it is just the reason, so amber keeps
          meaning "there is something for you to do here". */}
      {entry.Suggestion ? (
        <p className="layer-drift-entry-suggestion">
          <Icon src="bulb" size={10} className="mt-0.5 shrink-0" />
          <span>{entry.Suggestion}</span>
        </p>
      ) : (
        <p className="layer-drift-entry-reason">{entry.Reason}</p>
      )}

      {entry.Remediation ? (
        <LayerDriftResolveActions remediation={entry.Remediation} />
      ) : null}
    </article>
  )
}

export default LayerDriftEntryRow
